import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ComparisonPanel = ({ compareProperties, onRemoveFromCompare, onClearAll, isVisible }) => {
  if (!isVisible || compareProperties.length === 0) return null;

  const getComparisonValue = (property, key) => {
    switch (key) {
      case 'price':
        return `₹${property.price.toLocaleString()}/month`;
      case 'area':
        return `${property.area} sqft`;
      case 'bedrooms':
        return `${property.bedrooms} Bed`;
      case 'bathrooms':
        return `${property.bathrooms} Bath`;
      case 'furnishing':
        return property.furnishing || 'Not specified';
      case 'parking':
        return property.amenities.includes('parking') ? 'Available' : 'Not available';
      case 'security':
        return property.amenities.includes('security') ? 'Yes' : 'No';
      case 'gym':
        return property.amenities.includes('gym') ? 'Yes' : 'No';
      default:
        return 'N/A';
    }
  };

  const comparisonFields = [
    { key: 'price', label: 'Monthly Rent', icon: 'IndianRupee' },
    { key: 'area', label: 'Area', icon: 'Square' },
    { key: 'bedrooms', label: 'Bedrooms', icon: 'Bed' },
    { key: 'bathrooms', label: 'Bathrooms', icon: 'Bath' },
    { key: 'furnishing', label: 'Furnishing', icon: 'Sofa' },
    { key: 'parking', label: 'Parking', icon: 'Car' },
    { key: 'security', label: 'Security', icon: 'Shield' },
    { key: 'gym', label: 'Gym', icon: 'Dumbbell' }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border shadow-modal">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="GitCompare" size={20} className="text-primary" />
            <h3 className="font-semibold text-text-primary">
              Compare Properties ({compareProperties.length}/3)
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              iconName="Trash2"
              iconPosition="left"
            >
              Clear All
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Eye"
              iconPosition="left"
            >
              View Comparison
            </Button>
          </div>
        </div>

        {/* Comparison Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Property Headers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {compareProperties.map((property) => (
                <div key={property.id} className="bg-muted rounded-lg p-3 relative">
                  <button
                    onClick={() => onRemoveFromCompare(property.id)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-surface hover:bg-error hover:text-error-foreground transition-quick"
                  >
                    <Icon name="X" size={14} />
                  </button>
                  
                  <div className="flex items-start space-x-3">
                    <Image
                      src={property.images[0]}
                      alt={property.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <Link to={`/property-detail-page?id=${property.id}`}>
                        <h4 className="font-medium text-text-primary hover:text-primary transition-quick line-clamp-1">
                          {property.title}
                        </h4>
                      </Link>
                      <p className="text-sm text-text-secondary line-clamp-1">{property.location}</p>
                      <p className="text-lg font-bold text-primary mt-1">
                        ₹{property.price.toLocaleString()}/month
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Add More Placeholder */}
              {compareProperties.length < 3 && (
                <div className="bg-muted/50 border-2 border-dashed border-border rounded-lg p-3 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Plus" size={24} className="text-text-secondary mx-auto mb-2" />
                    <p className="text-sm text-text-secondary">Add property to compare</p>
                  </div>
                </div>
              )}
            </div>

            {/* Comparison Table */}
            <div className="bg-surface rounded-lg border border-border overflow-hidden">
              {comparisonFields.map((field, index) => (
                <div
                  key={field.key}
                  className={`grid grid-cols-4 ${index % 2 === 0 ? 'bg-muted/30' : 'bg-surface'}`}
                >
                  {/* Field Label */}
                  <div className="p-3 border-r border-border">
                    <div className="flex items-center space-x-2">
                      <Icon name={field.icon} size={16} className="text-text-secondary" />
                      <span className="text-sm font-medium text-text-primary">{field.label}</span>
                    </div>
                  </div>
                  
                  {/* Property Values */}
                  {[0, 1, 2].map((propertyIndex) => (
                    <div key={propertyIndex} className="p-3 border-r border-border last:border-r-0">
                      {compareProperties[propertyIndex] ? (
                        <span className="text-sm text-text-primary">
                          {getComparisonValue(compareProperties[propertyIndex], field.key)}
                        </span>
                      ) : (
                        <span className="text-sm text-text-secondary">-</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center space-x-3 mt-4">
              {compareProperties.map((property) => (
                <Link
                  key={property.id}
                  to={`/property-detail-page?id=${property.id}`}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="left"
                  >
                    View Details
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;